import React from 'react';
import {
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  List,
  ListItem,
  ListItemText,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Autocomplete,
  TextField
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CategoryIcon from '@mui/icons-material/Category';
import SettingsIcon from '@mui/icons-material/Settings';
import { useTranslation } from 'react-i18next';

// Yazıcı tanımlama için tip
interface DefinedPrinter {
  id: string;
  name: string;
}

// Kategori tipi
interface InventoryCategory {
  id: string;
  name: string;
  parentId: string | null;
  level: number;
  createdAt: string;
  branchesId?: string | null;
}

interface PrintingSettingsTabProps {
  // Yazdırma ayarları
  printingMode: 'single' | 'category';
  defaultReceiptPrinter: DefinedPrinter | null;
  definedPrinters: DefinedPrinter[];
  categoryPrinterAssignments: { [categoryId: string]: string | null };
  
  // Seçilmiş kategorilerin listesi ve bu kategorilere ait veriler
  uniqueCategoryIds: string[];
  categoriesToListForPrinting: InventoryCategory[];
  
  // Yerel yazıcı işlemleri
  localPrinters: DefinedPrinter[];
  loadingLocalPrinters: boolean;
  localPrintersError: string | null;
  
  // Event handler'lar
  onPrintingModeChange: (event: React.ChangeEvent<HTMLInputElement>, newMode: string) => void;
  onSetDefaultPrinter: (printer: DefinedPrinter) => void;
  onRemoveDefaultPrinter: () => void;
  onCategoryPrinterChange: (categoryId: string, printerId: string | null) => void;
  onRefreshLocalPrinters: () => void;
  onPrintTestPage: (printerId: string, printerName: string) => void;
  
  // Test sayfası yazdırma durumu
  printTestLoading: string | null;

  // Fiş ayarları modalı için event handler
  onOpenReceiptSettingsModal: () => void;
  
  // Fiş numarası sıfırlama fonksiyonu
  onResetReceiptNumber?: () => void;
  
  // Fiş numarası ayarlarını gösterme durumu
  receiptNumberEnabled?: boolean;
}

const PrintingSettingsTab: React.FC<PrintingSettingsTabProps> = ({
  printingMode,
  defaultReceiptPrinter,
  definedPrinters,
  categoryPrinterAssignments,
  uniqueCategoryIds,
  categoriesToListForPrinting,
  localPrinters,
  loadingLocalPrinters,
  localPrintersError,
  onPrintingModeChange,
  onSetDefaultPrinter,
  onRemoveDefaultPrinter,
  onCategoryPrinterChange,
  onRefreshLocalPrinters,
  onPrintTestPage,
  printTestLoading,
  onOpenReceiptSettingsModal,
  onResetReceiptNumber,
  receiptNumberEnabled = false
}) => {
  const { t } = useTranslation('salesSystem');

  return (
    <Paper elevation={0} sx={{ p: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1">{t('printingSettingsTab.title')}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            size="small"
            startIcon={<SettingsIcon />}
            onClick={onOpenReceiptSettingsModal}
          >
            {t('printingSettingsTab.receiptSettingsButton')}
          </Button>
        </Box>
      </Box>
      
      <FormControl component="fieldset" sx={{ mb: 2 }}>
        <RadioGroup
          row
          aria-label={t('printingSettingsTab.mode.ariaLabel')}
          name="printing-mode-radio-buttons-group"
          value={printingMode}
          onChange={onPrintingModeChange}
        >
          <FormControlLabel 
            value="single" 
            control={<Radio size="small" />} 
            label={<Box sx={{display: 'flex', alignItems: 'center'}}><PrintIcon sx={{mr: 0.5, fontSize: '1rem'}}/> {t('printingSettingsTab.mode.single')}</Box>} 
          />
          <FormControlLabel 
            value="category" 
            control={<Radio size="small" />} 
            label={<Box sx={{display: 'flex', alignItems: 'center'}}><CategoryIcon sx={{mr: 0.5, fontSize: '1rem'}}/> {t('printingSettingsTab.mode.category')}</Box>} 
            sx={{ml:2}}
          />
        </RadioGroup>
      </FormControl>
      <Divider sx={{ mb: 2 }}/>

      {/* Tek Yazıcı Modu İçeriği */}
      <Box sx={{ opacity: printingMode === 'single' ? 1 : 0.5, pointerEvents: printingMode === 'single' ? 'auto' : 'none' }}>
        {printingMode === 'single' && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              {defaultReceiptPrinter ? (
                <Paper variant="outlined" sx={{p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <Typography><strong>{defaultReceiptPrinter.name}</strong></Typography>
                  <Button variant="outlined" color="error" size="small" onClick={onRemoveDefaultPrinter}>{t('printingSettingsTab.common.remove')}</Button>
                </Paper>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{mb:1}}> 
                  {t('printingSettingsTab.singleMode.selectDefaultHint')}
                </Typography>
              )}

              {/* Local Printers Section for Single Mode */} 
              <Typography variant="h6" sx={{ mt: 2.5, mb: 1, fontSize: '1rem' }}>{t('printingSettingsTab.localPrinters.title')}</Typography>
              <Button 
                size="small" 
                variant="outlined"
                onClick={onRefreshLocalPrinters}
                sx={{mb: 1.5}}
                disabled={loadingLocalPrinters}
              >
                {loadingLocalPrinters ? <CircularProgress size={16} sx={{mr:1}} /> : null}
                {t('printingSettingsTab.localPrinters.refresh')}
              </Button>

              {loadingLocalPrinters && !localPrintersError && <CircularProgress size={20} />}
              {localPrintersError && <Alert severity="error" sx={{mb:1}}>{localPrintersError}</Alert>}
              {!loadingLocalPrinters && !localPrintersError && localPrinters.length === 0 && (
                <Typography variant="caption" color="text.secondary">{t('printingSettingsTab.localPrinters.notFound')}</Typography>
              )}
              {!loadingLocalPrinters && !localPrintersError && localPrinters.length > 0 && (
                <List dense disablePadding component={Paper} variant="outlined" sx={{maxHeight: 200, overflow: 'auto'}}>
                  {localPrinters.map(printer => (
                    <ListItem 
                      key={printer.id} 
                      divider
                      secondaryAction={
                        <Box>
                          <Button 
                            size="small" 
                            variant="text"
                            color="primary"
                            onClick={() => onPrintTestPage(printer.id, printer.name)}
                            disabled={printTestLoading === printer.id}
                            sx={{fontSize: '0.7rem', p: '2px 4px', mr: 1}}
                          >
                            {printTestLoading === printer.id ? t('printingSettingsTab.localPrinters.printing') : t('printingSettingsTab.localPrinters.test')}
                          </Button>
                          <Button 
                            size="small" 
                            variant="text"
                            onClick={() => onSetDefaultPrinter(printer)}
                            disabled={defaultReceiptPrinter?.name === printer.name}
                            sx={{fontSize: '0.7rem', p: '2px 4px'}}
                          >
                            {defaultReceiptPrinter?.name === printer.name ? t('printingSettingsTab.localPrinters.default') : t('printingSettingsTab.localPrinters.set')}
                          </Button>
                        </Box>
                      }
                      sx={{ pl:1.5, pr:1, py: 0.5 }}
                    >
                      <ListItemText primary={printer.name} sx={{m:0}}/>
                    </ListItem>
                  ))}
                </List>
              )}
            </Grid>
            <Grid item xs={12} md={7}>
              <Typography variant="body2" color="text.secondary">
                {t('printingSettingsTab.singleMode.description')}
              </Typography>
            </Grid>
          </Grid>
        )}
      </Box>
      
      {/* Kategori Bazlı Mod İçeriği */}
      <Box sx={{ mt: 3, opacity: printingMode === 'category' ? 1 : 0.5, pointerEvents: printingMode === 'category' ? 'auto' : 'none' }}>
        <Typography variant="h6" sx={{ mb: 1, fontSize: '1rem', color: printingMode === 'category' ? 'text.primary' : 'text.disabled' }}>
          {t('printingSettingsTab.categoryMode.title')}
        </Typography>
        {printingMode === 'category' && (
          <Grid container spacing={3}>
            <Grid item xs={12}> 
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {t('printingSettingsTab.categoryMode.description')}
              </Typography>

              <Button 
                size="small" 
                variant="outlined"
                onClick={onRefreshLocalPrinters}
                sx={{mb: 1.5}}
                disabled={loadingLocalPrinters}
              >
                {loadingLocalPrinters ? <CircularProgress size={16} sx={{mr:1}} /> : null}
                {t('printingSettingsTab.localPrinters.refresh')}
              </Button>

              {loadingLocalPrinters && !localPrintersError && <CircularProgress size={20} sx={{display: 'block', margin: 'auto'}}/>}
              {localPrintersError && <Alert severity="error" sx={{mb:1.5}}>{localPrintersError}</Alert>}
              
              {/* Kategori-Yazıcı ataması tablosu */}
              {!loadingLocalPrinters && !localPrintersError && categoriesToListForPrinting.length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
                        <TableCell>{t('printingSettingsTab.table.categoryName')}</TableCell>
                        <TableCell sx={{width: '60%'}}>{t('printingSettingsTab.table.assignedPrinter')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {categoriesToListForPrinting.map(category => (
                        <TableRow key={category.id}>
                          <TableCell component="th" scope="row">{category.name}</TableCell>
                          <TableCell>
                            <Autocomplete
                              size="small"
                              options={localPrinters} 
                              getOptionLabel={(option) => option.name}
                              value={localPrinters.find(p => p.id === categoryPrinterAssignments[category.id]) || null}
                              onChange={(event, newValue) => {
                                onCategoryPrinterChange(category.id, newValue ? newValue.id : null);
                              }}
                              renderInput={(params) => 
                                <TextField 
                                  {...params} 
                                  variant="standard" 
                                  placeholder={localPrinters.length > 0 ? t('printingSettingsTab.table.selectPrinter') : t('printingSettingsTab.table.refreshPrintersFirst')} 
                                />
                              }
                              sx={{ minWidth: 250 }}
                              ListboxProps={{ style: { maxHeight: 180, overflow: 'auto' } }}
                              disabled={localPrinters.length === 0 && !loadingLocalPrinters} 
                              noOptionsText={loadingLocalPrinters ? t('printingSettingsTab.common.loading') : t('printingSettingsTab.localPrinters.notFound')}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Grid>
          </Grid>
        )}
      </Box>
    </Paper>
  );
};

export default PrintingSettingsTab;
